import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import SEO from "@/components/SEO";

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState("checking"); // checking | done | invalid | error
  const [email, setEmail] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    // Guard against React 18 StrictMode double-invoke and against
    // re-mount reloads consuming the token twice.
    if (startedRef.current) return;
    startedRef.current = true;
    api
      .get(`/newsletter/unsubscribe?token=${encodeURIComponent(token)}`)
      .then((r) => {
        setEmail(r.data.email);
        setState("done");
      })
      .catch((err) => {
        if (err?.response?.status === 404) setState("invalid");
        else setState("error");
      });
  }, [token]);

  return (
    <section className="py-24 min-h-[60vh]">
      <SEO title="Unsubscribe" description="Unsubscribe from the HOFA newsletter." path="/unsubscribe" />
      <div className="max-w-[560px] mx-auto px-6 text-center" data-testid="unsubscribe-page">
        {state === "checking" && (
          <>
            <Loader2 className="animate-spin mx-auto text-palm" size={40} />
            <h1 className="font-display text-3xl mt-6 mb-2">Unsubscribing…</h1>
            <p className="text-ink/70">One moment while we remove you from the list.</p>
          </>
        )}
        {state === "done" && (
          <>
            <CheckCircle2 className="mx-auto text-palm" size={56} />
            <h1 className="font-display text-3xl mt-6 mb-3">You're unsubscribed.</h1>
            {email && <p className="text-ink/80 mb-1">We've removed <strong>{email}</strong> from the HOFA newsletter.</p>}
            <p className="text-ink/60 mt-2">Change your mind? You can resubscribe any time from the site footer.</p>
            <Link to="/" className="inline-block mt-8 bg-palm text-white font-bold rounded-full px-6 py-3 no-underline hover:shadow-xl transition" data-testid="unsubscribe-home-btn">
              Back to HOFA
            </Link>
          </>
        )}
        {(state === "invalid" || state === "error") && (
          <>
            <XCircle className="mx-auto text-berry" size={56} />
            <h1 className="font-display text-3xl mt-6 mb-3">
              {state === "invalid" ? "Link no longer valid" : "Something went wrong"}
            </h1>
            <p className="text-ink/70 mb-6">
              {state === "invalid"
                ? "This unsubscribe link is no longer valid — you may already be unsubscribed."
                : "We couldn't process your request. Please try again in a moment."}
            </p>
            <Link to="/contact" className="inline-block border-2 border-palm text-palm font-bold rounded-full px-6 py-3 no-underline hover:bg-palm hover:text-white transition">
              Contact HOFA
            </Link>
          </>
        )}
      </div>
    </section>
  );
}
